import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useClient } from 'urql';
import CreateRuleModal from '../../../../../configuracion2/createRuleModal/CreateRuleModal';
import CreateContactRuleModal from '../../../../../configuracion2/createContactRuleModal/CreateContactRuleModal';
import { CreateRule } from '../../../../../../graphql/rules';
import { importAlertState } from '../../../../../../redux/actions/alert';
import { useRules } from '../../../../../../context/RulesContext';
import { resolveAgendaIdByRut } from './journalEntryRuleSave';
import {
  clearJournalEntryRuleSaveOffer,
  registerJournalEntryRuleSaveHost,
} from './journalEntryRuleSaveBridge';

const buildCreateRuleInitialValues = (offer) => {
  if (offer?.ruleInput?.ruleType === 'MATCH') {
    return {
      description: offer.ruleInput.description || '',
      transactionType: offer.ruleInput.transactionType || 'ALL',
      type: offer.ruleInput.type || '',
      typeLabel: offer.ruleInput.typeLabel || '',
      accountKey: offer.ruleInput.accountKey || offer.account?.accountKey || '',
      accountName: offer.ruleInput.accountName || offer.account?.accountName || '',
    };
  }

  return {
    accountKey: offer?.account?.accountKey || '',
    accountName: offer?.account?.accountName || '',
  };
};

const buildContactRuleContext = (offer) => {
  const { ruleInput } = offer || {};
  if (!ruleInput || ruleInput.ruleType !== 'DOCUMENT') return null;

  return {
    agenda: {
      id: ruleInput.agendaId || null,
      name: ruleInput.contactName || ruleInput.contactRut || '',
      rut: ruleInput.contactRut || '',
    },
    category: ruleInput.isReceived ? 'provider' : 'client',
    initialFormValues: {
      description: ruleInput.description || '',
      accountKey: ruleInput.accountKey || offer.account?.accountKey || '',
      accountName: ruleInput.accountName || offer.account?.accountName || '',
    },
  };
};

const JournalEntryRuleSaveHost = () => {
  const { t } = useTranslation('accounting');
  const dispatch = useDispatch();
  const urqlClient = useClient();
  const { selectedCompany } = useSelector((state) => state.companyList);
  const { refetchRules } = useRules();
  const { executeMutation: createRuleMutation } = CreateRule();

  const [activeOffer, setActiveOffer] = useState(null);
  const [showCreateRuleModal, setShowCreateRuleModal] = useState(false);
  const [showContactRuleModal, setShowContactRuleModal] = useState(false);
  const [createRuleInitialValues, setCreateRuleInitialValues] = useState(null);
  const [contactRuleContext, setContactRuleContext] = useState(null);
  const [isSavingRule, setIsSavingRule] = useState(false);

  const closeAll = useCallback(() => {
    setShowCreateRuleModal(false);
    setShowContactRuleModal(false);
    setActiveOffer(null);
    setCreateRuleInitialValues(null);
    setContactRuleContext(null);
    clearJournalEntryRuleSaveOffer();
  }, []);

  const openFromOffer = useCallback((offer) => {
    setActiveOffer(offer);
    if (offer?.ruleInput?.ruleType === 'DOCUMENT') {
      setContactRuleContext(buildContactRuleContext(offer));
      setShowContactRuleModal(true);
      return;
    }
    setCreateRuleInitialValues(buildCreateRuleInitialValues(offer));
    setShowCreateRuleModal(true);
  }, []);

  useEffect(() => registerJournalEntryRuleSaveHost(openFromOffer), [openFromOffer]);

  const persistRule = async (ruleInput) => {
    if (!selectedCompany?.id || !ruleInput) return false;

    setIsSavingRule(true);
    try {
      let input = { ...ruleInput };
      if (!input.agendaId && input.contactRut && input.ruleType === 'DOCUMENT') {
        const agendaId = await resolveAgendaIdByRut(
          urqlClient,
          selectedCompany.id,
          input.contactRut,
        );
        if (!agendaId) {
          dispatch(
            importAlertState({
              state: 'warning',
              content: t('journalEntry.saveRuleNoContact'),
              duration: 5,
            }),
          );
          return false;
        }
        input = { ...input, agendaId: String(agendaId) };
      }

      const { data, error } = await createRuleMutation({
        companyId: selectedCompany.id,
        input,
      });

      if (error || !data?.createRule?.status) {
        dispatch(
          importAlertState({
            state: 'error',
            content: error?.message || data?.createRule?.message || t('journalEntry.saveRuleError'),
            duration: 4,
          }),
        );
        return false;
      }

      await refetchRules({ requestPolicy: 'network-only' });
      dispatch(
        importAlertState({
          state: 'success',
          content: t('journalEntry.saveRuleSuccess'),
          duration: 4,
        }),
      );
      return true;
    } catch (err) {
      dispatch(
        importAlertState({
          state: 'error',
          content: err?.message || t('journalEntry.saveRuleError'),
          duration: 4,
        }),
      );
      return false;
    } finally {
      setIsSavingRule(false);
    }
  };

  const handleCreateRuleModalSave = async (formData) => {
    if (!selectedCompany?.id) return;

    setIsSavingRule(true);
    try {
      const { data, error } = await createRuleMutation({
        companyId: selectedCompany.id,
        input: {
          description: formData.description,
          transactionType: formData.transactionType,
          type: formData.type,
          typeLabel: formData.typeLabel,
          accountKey: formData.accountKey,
          accountName: formData.accountName,
          exceptions: (formData.exceptions || [])
            .map((ex) => {
              if (
                ex.exceptionKind === 'BANK_PASSWORD_ID' &&
                ex.passwordId &&
                ex.accountKey &&
                ex.accountName
              ) {
                return {
                  filters: [{ kind: 'BANK_PASSWORD_ID', value: String(ex.passwordId) }],
                  accountKey: ex.accountKey,
                  accountName: ex.accountName,
                };
              }
              if (
                ex.exceptionKind === 'BANK_CREDIT_PRINCIPAL' &&
                ex.creditId &&
                ex.accountKey &&
                ex.accountName
              ) {
                return {
                  filters: [{ kind: 'BANK_CREDIT_PRINCIPAL', value: String(ex.creditId) }],
                  accountKey: ex.accountKey,
                  accountName: ex.accountName,
                };
              }
              if (ex.agendaId && ex.accountKey && ex.accountName) {
                return {
                  filters: [{ kind: 'AGENDA_ID', agendaId: String(ex.agendaId) }],
                  accountKey: ex.accountKey,
                  accountName: ex.accountName,
                };
              }
              return null;
            })
            .filter(Boolean),
        },
      });

      if (error || !data?.createRule?.status) {
        dispatch(
          importAlertState({
            state: 'error',
            content: error?.message || data?.createRule?.message || t('journalEntry.saveRuleError'),
            duration: 4,
          }),
        );
        return;
      }

      await refetchRules({ requestPolicy: 'network-only' });
      dispatch(
        importAlertState({
          state: 'success',
          content: t('journalEntry.saveRuleSuccess'),
          duration: 4,
        }),
      );
      closeAll();
    } catch (err) {
      dispatch(
        importAlertState({
          state: 'error',
          content: err?.message || t('journalEntry.saveRuleError'),
          duration: 4,
        }),
      );
    } finally {
      setIsSavingRule(false);
    }
  };

  const handleContactRuleModalSave = async (formData) => {
    const ruleInput = activeOffer?.ruleInput;
    if (!ruleInput || ruleInput.ruleType !== 'DOCUMENT') return;

    const input = {
      ruleType: 'DOCUMENT',
      description: formData.description,
      isReceived: ruleInput.isReceived,
      contactRut: ruleInput.contactRut,
      contactName: ruleInput.contactName,
      accountKey: formData.accountKey,
      accountName: formData.accountName,
      costCenterAllocations: formData.costCenterAllocations,
    };

    if (ruleInput.agendaId) {
      input.agendaId = String(ruleInput.agendaId);
    }

    if (ruleInput.isReceived && ruleInput.documentSubType) {
      input.documentSubType = ruleInput.documentSubType;
    }

    const ok = await persistRule(input);
    if (ok) closeAll();
  };

  return (
    <>
      <CreateRuleModal
        open={showCreateRuleModal}
        onClose={closeAll}
        onSave={handleCreateRuleModalSave}
        isSaving={isSavingRule}
        initialFormValues={createRuleInitialValues}
      />
      <CreateContactRuleModal
        open={showContactRuleModal}
        onClose={closeAll}
        onSave={handleContactRuleModalSave}
        isSaving={isSavingRule}
        agenda={contactRuleContext?.agenda}
        category={contactRuleContext?.category}
        companyId={selectedCompany?.id}
        initialFormValues={contactRuleContext?.initialFormValues}
      />
    </>
  );
};

export default JournalEntryRuleSaveHost;
